import React, { useState, useEffect, useRef } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { TextField } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import ClientAutocomplete from "./ClientAutocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import moment from "moment";
import "./AppointmentForm.scss";
import HelpButton from "../../../../../shared/components/tutorialModal/HelpButton";
import { isDateTimeAvailable } from "../../../../../shared/util/disponibilidadeCheck";
import toast from "react-hot-toast";
import DialogoConfirmacaoAgendamento from "../../../../../shared/components/confirmacaoPresenca/DialogoConfirmacaoAgendamento";
import { dadosFinanceirosTratamentoIsolado } from "../../../../../shared/util/dadosFinanceirosTratamentoIsolado";

// ─── constants ────────────────────────────────────────────────────────────────

const SLOT_COLORS = [
  { border: "#378ADD", bg: "#E6F1FB", label: "#0C447C" },
  { border: "#1D9E75", bg: "#E1F5EE", label: "#085041" },
  { border: "#EF9F27", bg: "#FAEEDA", label: "#633806" },
  { border: "#D4537E", bg: "#FBEAF0", label: "#4B1528" },
  { border: "#534AB7", bg: "#EEEDFE", label: "#26215C" },
];

const getFirstAndLastName = (fullName) => {
  if (!fullName) return "";
  const names = fullName.trim().split(" ");
  if (names.length === 1) return names[0];
  return `${names[0]} ${names[names.length - 1]}`;
};

const buildSlot = (overrides = {}) => ({
  id: crypto.randomUUID(),
  startDate: null,
  endDate: null,
  aviso: null,
  appointmentType: "procedimento",
  selectedClient: "",
  selectedProcedure: "",
  procedimentos: [],
  compromisso: "",
  observacoesAgendamento: "",
  tratamentoIsoladoComEntradaChecked: false,
  // lista espera — per slot
  listaEsperaAtiva: false,
  selectedListaEsperaItem: null,
  // new client form — per slot
  showNewClientForm: false,
  newClientData: { nome: "", endereco: "", contacto: "", genero: "" },
  ...overrides,
});

const verificarSobreposicao = (
  slotIndex,
  startDate,
  endDate,
  events,
  slots,
) => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (end <= start) return null;

  for (const ev of events) {
    if (ev.display === "background") continue;
    const evStart = new Date(ev.start).getTime();
    const evEnd = new Date(ev.end).getTime();
    if (start < evEnd && end > evStart) {
      return `Sobreposição com "${ev.title?.trim()}" (${moment(ev.start).format("HH:mm")} - ${moment(ev.end).format("HH:mm")})`;
    }
  }

  for (let i = 0; i < slots.length; i++) {
    if (i === slotIndex) continue;
    const other = slots[i];
    if (!other.startDate || !other.endDate) continue;
    const oStart = new Date(other.startDate).getTime();
    const oEnd = new Date(other.endDate).getTime();
    if (start < oEnd && end > oStart) {
      return `Sobreposição com a Sessão ${i + 1} (${moment(other.startDate).format("HH:mm")} - ${moment(other.endDate).format("HH:mm")})`;
    }
  }

  return null;
};

// ─── AppointmentForm (agenda normal — multi-slot) ─────────────────────────────

const AppointmentForm = ({
  isOpen,
  onClose,
  date,
  event,
  events,
  clients,
  clientesProcedimento,
  allTratamentos,
  allCategoriasTratamento,
  procedimentosPorMarcar,
  orcamentosPorMarcar,
  auth,
  sendRequest,
  clinica,
  updateEvents,
  medicoId,
  nomeMedico,
  listaEspera,
  onAgendarDaLista,
  duracaoConsulta,
  startLoading,
  stopLoading,
  disponibilidadeMedico,
  horarioAtendimento,
}) => {
  const [slots, setSlots] = useState([
    buildSlot({
      startDate: date,
      endDate: date ? new Date(date.getTime() + duracaoConsulta * 60000) : null,
    }),
  ]);
  const [menssagemDeErro, setMensagemDeErro] = useState("");
  const [dataLembrete1Mes, setDataLembrete1Mes] = useState(null);
  const [dataLembrete1Semana, setDataLembrete1Semana] = useState(null);
  const [dataLembrete1Dia, setDataLembrete1Dia] = useState(null);
  const [analiseClienteModal, setAnaliseClienteModal] = useState({
    show: false,
    alertas: [],
    onConfirm: null,
  });

  /* ─── A MARCAÇÃO ESTAVA CONFIRMADA E A DATA ESTÁ A MUDAR ───────────────────
   *
   * O cliente confirmou uma data que está a mudar; pergunta-se antes de gravar
   * e a resposta segue ao servidor em `manterConfirmacao`.
   *
   * Segue o mesmo padrão do `analiseClienteModal` logo acima: guarda-se a
   * resposta e repete-se o `handleSubmit`, em vez de a passar por dez funções.
   *
   * `perguntaConfirmacao` — `{ nomeCliente }` enquanto se espera, `null` fora.
   *
   * A RESPOSTA é um `ref` e não estado, e isso é o essencial: o
   * `handleSubmit` que o diálogo chama a seguir é o closure criado no render
   * ANTERIOR, e esse continuaria a ler o estado antigo. Resultado com estado:
   * o portão abria o diálogo outra vez e era preciso responder duas vezes.
   * Um `ref` lê-se sempre fresco, na mesma passagem. */
  const [perguntaConfirmacao, setPerguntaConfirmacao] = useState(null);
  const respostaConfirmacaoRef = useRef(null);

  /* ─── A TRAVA DO DUPLO CLIQUE ─────────────────────────────────────────────
   *
   * O véu do `startLoading` cobre o ecrã e bloqueia cliques — mas só sobe
   * dentro do `executarAgendamento`, e para os tipos que têm cliente há um
   * `await verificarAnaliseCliente` ANTES disso: um pedido HTTP inteiro com o
   * botão vivo. Dois cliques nessa janela criavam duas marcações completas,
   * com milissegundos de diferença no `created_at`.
   *
   * `useRef` e não `useState`: o estado só vale no render seguinte, e a janela
   * é precisamente essa. O `useState` ao lado não trava nada — serve só para o
   * botão poder mostrar que está a gravar.
   *
   * Liberta-se em todas as saídas que devolvem o formulário à pessoa (erro de
   * validação, pergunta da confirmação, aviso da análise) e no `finally` do
   * `executarAgendamento`. Esquecer uma delas deixa o formulário trancado para
   * sempre, que é pior do que o duplicado. */
  const aGravarRef = useRef(false);
  const [aGravar, setAGravar] = useState(false);

  const ocupar = () => {
    if (aGravarRef.current) return false;
    aGravarRef.current = true;
    setAGravar(true);
    return true;
  };

  const libertar = () => {
    aGravarRef.current = false;
    setAGravar(false);
  };

  /* O formulário não desmonta entre edições — fica montado com `isOpen`. Sem
     isto, a resposta dada numa marcação valia para a seguinte. */
  useEffect(() => {
    if (isOpen) respostaConfirmacaoRef.current = null;
  }, [isOpen]);

  // ── slot CRUD ─────────────────────────────────────────────────────────────

  const updateSlot = (index, patch) =>
    setSlots((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );

  const removeSlot = (index) =>
    setSlots((prev) => prev.filter((_, i) => i !== index));

  const addSlot = () => {
    const last = slots[slots.length - 1];
    const newStart = last.endDate
      ? new Date(last.endDate.getTime())
      : date
        ? new Date(date.getTime())
        : null;
    const newEnd = newStart
      ? new Date(newStart.getTime() + duracaoConsulta * 60000)
      : null;
    setSlots((prev) => [
      ...prev,
      buildSlot({
        startDate: newStart,
        endDate: newEnd,
        appointmentType: last.appointmentType,
        selectedClient: last.listaEsperaAtiva ? "" : last.selectedClient,
        selectedProcedure: last.listaEsperaAtiva ? "" : last.selectedProcedure,
        procedimentos: last.listaEsperaAtiva ? [] : last.procedimentos,
        observacoesAgendamento: last.listaEsperaAtiva
          ? ""
          : last.observacoesAgendamento,
        tratamentoIsoladoComEntradaChecked: last.listaEsperaAtiva
          ? false
          : last.tratamentoIsoladoComEntradaChecked,
      }),
    ]);
  };

  // ── countProcedimentos ────────────────────────────────────────────────────

  const countProcedimentos = (clientId) => {
    const countMapPlanos = {};
    const countMapOrcamentos = {};
    const result = [];

    procedimentosPorMarcar.forEach((el) => {
      if (el.cliente?.id === clientId) {
        const key = `${el.procedimento.label}-${el.procedimento.id}-${el.id_plano}`;
        if (countMapPlanos[key]) countMapPlanos[key].count++;
        else
          countMapPlanos[key] = {
            label: el.procedimento.label,
            id: el.procedimento.id + "-" + el.id_plano,
            count: 1,
          };
      }
    });

    orcamentosPorMarcar.forEach((el) => {
      if (el.cliente?.id === clientId) {
        const key = `${el.procedimento.label}-${el.procedimento.id}-${el.id_orcamento}`;
        if (countMapOrcamentos[key]) countMapOrcamentos[key].count++;
        else
          countMapOrcamentos[key] = {
            label: el.procedimento.label,
            id: el.procedimento.id + "-orc-" + el.id_orcamento,
            count: 1,
            isOrcamento: true,
          };
      }
    });

    Object.values(countMapPlanos).forEach((item) =>
      result.push({
        label: `${item.count}x ${item.label}`,
        id: item.id,
        originalLabel: item.label,
        isOrcamento: false,
      }),
    );
    Object.values(countMapOrcamentos).forEach((item) =>
      result.push({
        label: `${item.count}x ${item.label} (Orçamento)`,
        id: item.id,
        originalLabel: item.label,
        isOrcamento: true,
      }),
    );
    return result;
  };

  // ── useEffect init ────────────────────────────────────────────────────────

  useEffect(() => {
    setMensagemDeErro("");

    if (event) {
      const type = event.extendedProps.isEvaluation
        ? "avaliacao"
        : event.extendedProps.isCompromisso
          ? "compromisso"
          : event.extendedProps.isTratamentoIsolado
            ? "tratamentoIsolado"
            : "procedimento";

      const slot = buildSlot({
        startDate: new Date(event.start.getTime()),
        endDate: new Date(event.end.getTime()),
        appointmentType: type,
        compromisso: event.extendedProps.isCompromisso
          ? event.title.trim()
          : "",
        observacoesAgendamento:
          event.extendedProps.observacoesAgendamento || "",
        tratamentoIsoladoComEntradaChecked:
          event.extendedProps.comEntrada || false,
      });

      if (event.extendedProps.clientId) {
        const clientData = clients.find(
          (c) => c.id === event.extendedProps.clientId,
        );
        slot.selectedClient = clientData || "";
        if (type === "tratamentoIsolado") {
          // Sem entrada → categorias; com entrada → tratamentos (têm preço)
          slot.procedimentos = event.extendedProps.comEntrada
            ? allTratamentos
            : allCategoriasTratamento;
          slot.selectedProcedure = event.extendedProps.procedureId || "";
        } else if (type === "procedimento") {
          slot.procedimentos = countProcedimentos(clientData?.id);
          slot.selectedProcedure = event.extendedProps.isOrcamento
            ? event.extendedProps.procedureId +
              "-orc-" +
              event.extendedProps.orcamentoId
            : event.extendedProps.procedureId +
              "-" +
              event.extendedProps.planoId;
        }
      }
      setSlots([slot]);
    } else {
      const startD = date ? new Date(date.getTime()) : null;
      const endD = startD
        ? new Date(startD.getTime() + duracaoConsulta * 60000)
        : null;
      setSlots([buildSlot({ startDate: startD, endDate: endD })]);
      setMensagemDeErro("");
      if (startD) {
        setDataLembrete1Mes(moment(startD).subtract(1, "month").toDate());
        setDataLembrete1Semana(moment(startD).subtract(1, "week").toDate());
        setDataLembrete1Dia(moment(startD).subtract(1, "day").toDate());
      } else {
        setDataLembrete1Mes(null);
        setDataLembrete1Semana(null);
        setDataLembrete1Dia(null);
      }
    }
  }, [
    clientesProcedimento,
    date,
    duracaoConsulta,
    event,
    procedimentosPorMarcar,
  ]);

  // ── per-slot handlers ─────────────────────────────────────────────────────

  const handleAppointmentTypeChange = (index, newType) => {
    const slot = slots[index];
    const listaItem = slot.selectedListaEsperaItem;

    let patch = {
      appointmentType: newType,
      selectedProcedure: "",
      tratamentoIsoladoComEntradaChecked: false,
    };

    if (listaItem && !listaItem.pacienteProvisorio) {
      if (newType === "procedimento") {
        patch.procedimentos = countProcedimentos(slot.selectedClient?.id);
      } else if (newType === "tratamentoIsolado") {
        // Sem entrada (default ao mudar de tipo) → categorias
        patch.procedimentos = allCategoriasTratamento;
        if (listaItem.categoriaTratamento)
          patch.selectedProcedure = listaItem.categoriaTratamento._id;
      } else {
        patch.procedimentos = [];
      }
    } else if (listaItem?.pacienteProvisorio && newType === "procedimento") {
      setMensagemDeErro("Paciente provisório não possui plano de tratamento.");
      return;
    } else {
      patch.selectedClient = "";
      if (newType === "procedimento") patch.procedimentos = [];
      else if (newType === "tratamentoIsolado")
        patch.procedimentos = allCategoriasTratamento;
      else patch.procedimentos = [];
    }

    updateSlot(index, patch);
  };

  const handleClienteChange = (index, newValue) => {
    // Para tratamento isolado a lista (categorias ou tratamentos, conforme "com
    // entrada") NÃO depende do cliente. Se resetássemos para [] ao limpar o
    // cliente, o Select (renderizado só quando procedimentos.length > 0)
    // desaparecia e não voltava ao reselecionar o cliente.
    const isIsolado = slots[index].appointmentType === "tratamentoIsolado";
    const isoladoList = slots[index].tratamentoIsoladoComEntradaChecked
      ? allTratamentos
      : allCategoriasTratamento;

    if (!newValue) {
      updateSlot(index, {
        selectedClient: "",
        selectedProcedure: "",
        procedimentos: isIsolado ? isoladoList : [],
      });
      return;
    }
    const procs = isIsolado
      ? isoladoList
      : slots[index].appointmentType === "procedimento"
        ? countProcedimentos(newValue.id)
        : slots[index].procedimentos;
    updateSlot(index, {
      selectedClient: newValue,
      selectedProcedure: "",
      procedimentos: procs,
    });
  };

  const handleEntradaChange = (index, checked) => {
    // Com entrada → tratamentos (têm preço); sem entrada → categorias
    updateSlot(index, {
      tratamentoIsoladoComEntradaChecked: checked,
      selectedProcedure: "",
      procedimentos: checked ? allTratamentos : allCategoriasTratamento,
    });
  };

  // ── lista espera per slot ─────────────────────────────────────────────────

  const handleToggleListaEspera = (index, checked) => {
    if (!checked) {
      updateSlot(index, {
        listaEsperaAtiva: false,
        selectedListaEsperaItem: null,
        selectedClient: "",
        selectedProcedure: "",
        procedimentos:
          slots[index].appointmentType === "tratamentoIsolado"
            ? slots[index].tratamentoIsoladoComEntradaChecked
              ? allTratamentos
              : allCategoriasTratamento
            : [],
        showNewClientForm: false,
        newClientData: {
          nome: "",
          endereco: "",
          contacto: "",
          genero: "",
        },
        observacoesAgendamento: "",
      });
      return;
    }
    updateSlot(index, { listaEsperaAtiva: true });
  };

  const handleSelectListaEsperaItem = (index, item) => {
    const clienteId = item.pacienteProvisorio
      ? item.pacienteProvisorio.contacto
      : item.cliente?._id;

    const jaExiste = slots.some((s, i) => {
      if (i === index || !s.selectedListaEsperaItem) return false;
      const outroClienteId = s.selectedListaEsperaItem.pacienteProvisorio
        ? s.selectedListaEsperaItem.pacienteProvisorio.contacto
        : s.selectedListaEsperaItem.cliente?._id;
      return outroClienteId === clienteId;
    });

    if (jaExiste) {
      setMensagemDeErro(
        "Este cliente já tem um agendamento da lista de espera noutra sessão.",
      );
      return;
    }

    const patch = {
      selectedListaEsperaItem: item,
      showNewClientForm: false,
      newClientData: {
        nome: "",
        endereco: "",
        contacto: "",
        genero: "",
      },
      selectedProcedure: "",
      tratamentoIsoladoComEntradaChecked: false,
      observacoesAgendamento: item.observacoes || "",
    };

    if (item.pacienteProvisorio) {
      patch.showNewClientForm = true;
      patch.newClientData = {
        nome: item.pacienteProvisorio.nome,
        contacto: item.pacienteProvisorio.contacto,
        genero: item.pacienteProvisorio.genero || "",
        endereco: "",
      };
      patch.appointmentType = "tratamentoIsolado";
      patch.procedimentos = allCategoriasTratamento;
      patch.selectedClient = "";
      if (item.categoriaTratamento)
        patch.selectedProcedure = item.categoriaTratamento._id;
    } else {
      const cli = clients.find((c) => c.id === item.cliente._id) || {
        id: item.cliente._id,
        label: item.cliente.nome,
      };
      patch.selectedClient = cli;
      const procs = countProcedimentos(item.cliente._id);
      const match = item.categoriaTratamento
        ? procs.find((p) => p.id.startsWith(item.categoriaTratamento._id))
        : null;
      if (match) {
        patch.appointmentType = "procedimento";
        patch.procedimentos = procs;
        patch.selectedProcedure = match.id;
      } else {
        patch.appointmentType = "tratamentoIsolado";
        patch.procedimentos = allCategoriasTratamento;
        if (item.categoriaTratamento)
          patch.selectedProcedure = item.categoriaTratamento._id;
      }
    }

    updateSlot(index, patch);
  };

  // ── new client form per slot ──────────────────────────────────────────────

  const handleToggleNewClientForm = (index) => {
    const slot = slots[index];
    const next = !slot.showNewClientForm;
    updateSlot(index, {
      showNewClientForm: next,
      selectedClient: next ? "" : slot.selectedClient,
      newClientData: {
        nome: "",
        endereco: "",
        contacto: "",
        genero: "",
      },
    });
    if (next)
      document.dispatchEvent(
        new MouseEvent("mousedown", {
          bubbles: true,
          cancelable: true,
          view: window,
        }),
      );
  };

  // ── validation ────────────────────────────────────────────────────────────

  const validarAgendamento = () => {
    let erro = "";
    const p = (i) => (slots.length > 1 ? `Sessão ${i + 1}: ` : "");

    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];

      if (!s.startDate || !moment(s.startDate).isValid())
        erro += `${p(i)}Data inicial inválida. `;
      if (!s.endDate || !moment(s.endDate).isValid())
        erro += `${p(i)}Data final inválida. `;
      if (
        s.startDate &&
        s.endDate &&
        moment(s.endDate).isSameOrBefore(moment(s.startDate))
      )
        erro += `${p(i)}Data final não pode ser igual ou anterior à inicial. `;

      if (s.showNewClientForm) {
        if (!s.newClientData.nome)
          erro += `${p(i)}Preencha o nome do cliente. `;
        if (!s.newClientData.contacto) erro += `${p(i)}Preencha o contacto. `;
        if (!s.newClientData.endereco) erro += `${p(i)}Preencha o endereço. `;
        if (!s.newClientData.data_nasc)
          erro += `${p(i)}Preencha a data de nascimento. `;
        if (!s.newClientData.genero)
          erro += `${p(i)}Selecione o género. `;
      }

      const needsClient = [
        "procedimento",
        "avaliacao",
        "tratamentoIsolado",
        "agendamentoProvisorio",
      ].includes(s.appointmentType);
      if (needsClient && !s.showNewClientForm && !s.selectedClient)
        erro += `${p(i)}Selecione um cliente. `;

      if (
        (s.appointmentType === "procedimento" ||
          s.appointmentType === "tratamentoIsolado") &&
        !s.selectedProcedure
      )
        erro += `${p(i)}Selecione um procedimento. `;

      if (s.appointmentType === "compromisso" && !s.compromisso)
        erro += `${p(i)}Preencha a descrição do compromisso. `;

      if (
        s.appointmentType !== "compromisso" &&
        disponibilidadeMedico &&
        s.startDate &&
        s.endDate
      ) {
        const endCheck = new Date(moment(s.endDate).valueOf() - 60000);
        if (
          !isDateTimeAvailable(
            s.startDate,
            disponibilidadeMedico,
            horarioAtendimento,
          )
        )
          erro += `${p(i)}Início fora da disponibilidade do médico. `;
        if (
          !isDateTimeAvailable(
            endCheck,
            disponibilidadeMedico,
            horarioAtendimento,
          )
        )
          erro += `${p(i)}Fim fora da disponibilidade do médico. `;
      }
    }

    if (slots[0]?.appointmentType === "agendamentoProvisorio") {
      const amanha = moment().add(1, "day").startOf("day");
      if (dataLembrete1Mes && moment(dataLembrete1Mes).isBefore(amanha))
        erro += "Lembrete 1 mês inválido. ";
      if (dataLembrete1Semana && moment(dataLembrete1Semana).isBefore(amanha))
        erro += "Lembrete 1 semana inválido. ";
      if (dataLembrete1Dia && moment(dataLembrete1Dia).isBefore(amanha))
        erro += "Lembrete 1 dia inválido. ";
    }

    const clientesListaEspera = [];
    for (let i = 0; i < slots.length; i++) {
      const item = slots[i].selectedListaEsperaItem;
      if (!item) continue;
      const cId = item.pacienteProvisorio
        ? item.pacienteProvisorio.contacto
        : item.cliente?._id;
      if (clientesListaEspera.includes(cId)) {
        erro += `${p(i)}Cliente já tem outro agendamento da lista de espera nesta submissão. `;
      } else {
        clientesListaEspera.push(cId);
      }
    }

    return erro;
  };

  // ── analise cliente ───────────────────────────────────────────────────────

  const verificarAnaliseCliente = async (clienteId) => {
    try {
      const tipo =
        clinica.clinica.configuracoes?.configuracoes?.tipoAgendamento?.tipo ||
        "Categoria de tratamento";
      const res = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/${clienteId}/analise-agendamento?tipoAgendamento=${encodeURIComponent(tipo)}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );
      return res;
    } catch {
      return { alertas: [] };
    }
  };

  // ── submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();

    /* O segundo clique sai aqui, sem fazer nada. */
    if (!ocupar()) return;

    const erro = validarAgendamento();
    if (erro) {
      setMensagemDeErro(erro);
      libertar();
      return;
    }
    setMensagemDeErro("");

    /* ─── O ✓ E A DATA QUE ESTÁ A MUDAR ─────────────────────────────────────
     *
     * Só quando TODAS se verificam: estamos a editar (`event`), a marcação
     * está confirmada, a data mudou, e ainda não se perguntou. Em qualquer
     * outro caso o formulário segue exactamente como seguia.
     *
     * Sem resposta, o servidor faz cair o ✓ — ficar confirmado sem o cliente
     * saber faz a agenda prometer um paciente que ninguém avisou. */
    if (
      event &&
      event.extendedProps?.confirmado &&
      !respostaConfirmacaoRef.current &&
      new Date(slots[0].startDate).getTime() !==
        new Date(event.start).getTime()
    ) {
      setPerguntaConfirmacao({ nomeCliente: event.title });
      /* O diálogo volta a chamar o `handleSubmit` quando a pessoa responder. */
      libertar();
      return;
    }

    const firstSlot = slots[0];
    const tiposComCliente = [
      "procedimento",
      "avaliacao",
      "tratamentoIsolado",
      "agendamentoProvisorio",
    ];
    if (
      tiposComCliente.includes(firstSlot.appointmentType) &&
      !firstSlot.showNewClientForm &&
      firstSlot.selectedClient?.id
    ) {
      const analise = await verificarAnaliseCliente(
        firstSlot.selectedClient.id,
      );
      if (analise.alertas?.length > 0) {
        setAnaliseClienteModal({
          show: true,
          alertas: analise.alertas,
          onConfirm: () => {
            setAnaliseClienteModal({
              show: false,
              alertas: [],
              onConfirm: null,
            });
            /* Este caminho não passa pelo `handleSubmit`, por isso ocupa a
               trava por si — senão o botão do aviso ficava sem proteção. */
            if (ocupar()) executarAgendamento();
          },
        });
        /* O aviso está aberto e o formulário volta a ser da pessoa. */
        libertar();
        return;
      }
    }
    executarAgendamento();
  };

  // ── updateListaEspera ─────────────────────────────────────────────────────

  const updateListaEspera = async (item) => {
    const formData = new FormData();
    formData.append("utilizador", auth.userId);
    formData.append("status", "agendado");
    await sendRequest(
      `${process.env.REACT_APP_BACKEND_LINK}/api/listaespera/updateStatusListaEspera/${item._id}`,
      "PATCH",
      formData,
      { Authorization: "Bearer " + auth.token },
    );
    onAgendarDaLista(item._id, item.prioridade);
  };

  // ── executarAgendamento ───────────────────────────────────────────────────

  const executarAgendamento = async () => {
    try {
      startLoading();

      if (event) {
        await processSlot(slots[0], true, null);
        if (slots[0].selectedListaEsperaItem)
          await updateListaEspera(slots[0].selectedListaEsperaItem);
        return;
      }

      let financeData = null;
      if (slots.some((s) => s.appointmentType === "tratamentoIsolado")) {
        const [catRes, caixaRes] = await Promise.all([
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/categoriasfinanceiro/clinica/entrada/${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/clinica/${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          ),
        ]);
        /* Lança com uma frase que diz o que falta e onde se cria, em vez de
           deixar um `undefined` rebentar duas linhas abaixo. */
        financeData = dadosFinanceirosTratamentoIsolado(caixaRes, catRes);
      }

      const resultados = { sucesso: 0, falha: 0, erros: [] };

      for (let i = 0; i < slots.length; i++) {
        try {
          await processSlot(slots[i], false, financeData);
          if (slots[i].selectedListaEsperaItem)
            await updateListaEspera(slots[i].selectedListaEsperaItem);
          resultados.sucesso++;
        } catch (err) {
          resultados.falha++;
          resultados.erros.push(
            `Sessão ${i + 1}: ${err.message || "Erro desconhecido"}`,
          );
          console.error(`Erro no slot ${i + 1}:`, err);
        }
      }

      if (resultados.sucesso > 0 && resultados.falha > 0) {
        toast.error(
          `${resultados.sucesso} de ${slots.length} sessões agendadas. Falharam: ${resultados.erros.join("; ")}`,
        );
      } else if (resultados.falha > 0 && resultados.sucesso === 0) {
        toast.error(
          "Nenhuma sessão foi agendada. " + resultados.erros.join("; "),
        );
      } else if (resultados.sucesso > 1) {
        toast.success(`${resultados.sucesso} sessões agendadas com sucesso!`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
      libertar();
      onClose();
    }
  };

  // ── processSlot ───────────────────────────────────────────────────────────

  const processSlot = async (slot, isEdit, financeData) => {
    const {
      appointmentType,
      startDate,
      endDate,
      selectedClient,
      selectedProcedure,
      procedimentos,
      compromisso,
      observacoesAgendamento,
      tratamentoIsoladoComEntradaChecked,
      showNewClientForm,
      newClientData,
      selectedListaEsperaItem,
    } = slot;

    const listaEsperaRef = selectedListaEsperaItem?._id || null;

    let tipoOriginal;
    if (isEdit && event) {
      tipoOriginal = event.extendedProps.isEvaluation
        ? "avaliacao"
        : event.extendedProps.isCompromisso
          ? "compromisso"
          : event.extendedProps.isTratamentoIsolado
            ? "tratamentoIsolado"
            : "procedimento";
    } else {
      tipoOriginal = false;
    }

    const houveAlteracao = tipoOriginal !== appointmentType && tipoOriginal;
    const isCreating = !isEdit || houveAlteracao;

    /* ── Um tratamento isolado PAGO não muda de tipo ─────────────────────────
     *
     * Gémea da guarda do `AppointmentFormTratamentos`. Alterar aqui obriga a
     * alterar lá — são as duas agendas, e o defeito é o mesmo nas duas.
     *
     * Trocar o tipo substitui o registo. Num isolado ainda não pago isso é
     * normal. Pago é outra coisa: tem uma entrada recebida, e o que acontecia
     * era criar-se o agendamento novo sem o isolado sair — ficavam os dois,
     * mesmo doente, mesma hora, com o dinheiro ligado a um deles.
     *
     * O evento só sabe `comEntrada`, não se foi recebida; pergunta-se ao
     * servidor, como o `handleDeleteEvent` já faz antes de apagar. */
    if (
      isEdit &&
      houveAlteracao &&
      tipoOriginal === "tratamentoIsolado" &&
      event?.extendedProps?.comEntrada
    ) {
      const isolado = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentoIsolados/${event.id}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );

      if (isolado.tratamentoIsolado?.entrada?.entradaRecebida) {
        toast.error(
          "Este tratamento isolado já foi recebido e não pode mudar de tipo de agendamento. " +
            "Transfira primeiro o pagamento, em Manutenção.",
        );
        return;
      }
    }

    // ── COMPROMISSO ──────────────────────────────────────────────────────────
    if (appointmentType === "compromisso") {
      const fd = new FormData();
      if (isEdit && !isCreating) {
        fd.append("compromisso", compromisso);
        fd.append("data_inicio_execucao", startDate);
        fd.append("data_fim_execucao", endDate);
        fd.append("utilizador", auth.userId);
        fd.append("nomeQuemAgendou", auth.nome);
        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/compromissos/alterardata/${event.id}`,
          "PATCH",
          fd,
          { Authorization: "Bearer " + auth.token },
        );
        updateEvents(
          {
            start: startDate,
            end: endDate,
            title: " " + compromisso,
            backgroundColor: "#F5F5F5",
            borderColor: "#616161",
            textColor: "#616161",
            extendedProps: { ...event.extendedProps, agendadoPor: auth.nome },
          },
          event.id,
        );
      } else {
        fd.append("medico", medicoId);
        fd.append("nomeMedico", nomeMedico);
        fd.append(
          "marcacao",
          JSON.stringify({
            compromisso,
            data_inicio_execucao: startDate,
            data_fim_execucao: endDate,
            agendadoPor: auth.userId,
            nomeQuemAgendou: auth.nome,
          }),
        );
        const res = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/compromissos/efetuarmarcacao/${clinica.clinica._id}`,
          "PATCH",
          fd,
          { Authorization: "Bearer " + auth.token },
        );
        updateEvents(
          {
            id: res.addedMarcacaoId,
            start: moment(startDate).format("YYYY-MM-DD HH:mm:ss"),
            end: moment(endDate).format("YYYY-MM-DD HH:mm:ss"),
            title: " " + compromisso,
            backgroundColor: "#F5F5F5",
            borderColor: "#616161",
            textColor: "#616161",
            extendedProps: { isCompromisso: true, agendadoPor: auth.nome },
          },
          null,
          null,
          houveAlteracao,
          event?.id,
        );
      }
      return;
    }

    // ── AVALIACAO ────────────────────────────────────────────────────────────
    if (appointmentType === "avaliacao") {
      const fd = new FormData();
      if (isEdit && !isCreating) {
        fd.append("cliente", selectedClient.id);
        fd.append("nomeCliente", selectedClient.label);
        fd.append("data_inicio_execucao", startDate);
        fd.append("data_fim_execucao", endDate);
        fd.append("observacoesAgendamento", observacoesAgendamento);
        fd.append("utilizador", auth.userId);
        fd.append("avaliacao", true);
        fd.append("nomeQuemAgendou", auth.nome);
        /* A resposta à pergunta do ✓. Ausente ou "false", o servidor faz
           cair a confirmação quando a data muda. */
        fd.append(
          "manterConfirmacao",
          respostaConfirmacaoRef.current === "manter" ? "true" : "false",
        );
        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/agenda/editarAgendamentoAvaliacao/${event.id}`,
          "PATCH",
          fd,
          { Authorization: "Bearer " + auth.token },
        );
        updateEvents(
          {
            start: startDate,
            end: endDate,
            title: ` ${getFirstAndLastName(selectedClient.label)} - Avaliação`,
            backgroundColor: "#fde6c5",
            borderColor: "#F57C00",
            textColor: "#F57C00",
            extendedProps: {
              ...event.extendedProps,
              clientId: selectedClient.id,
              agendadoPor: auth.nome,
              observacoesAgendamento: observacoesAgendamento || "",
              /* Sem isto, o ✓ ficava no ecrã depois de o servidor o ter
                 apagado, e só saía ao recarregar a página — a agenda a dizer
                 que o doente confirmou uma hora que já não é a dele.
                 `...event.extendedProps` traz o valor ANTIGO. */
              confirmado:
                respostaConfirmacaoRef.current === "retirar"
                  ? false
                  : event.extendedProps?.confirmado,
            },
          },
          event.id,
        );
        return;
      }
      fd.append("medico", medicoId);
      fd.append("nomeMedico", nomeMedico);
      fd.append(
        "marcacao",
        JSON.stringify({
          cliente: selectedClient.id,
          data_inicio_execucao: startDate,
          data_fim_execucao: endDate,
          avaliacao: true,
          agendadoPor: auth.userId,
          nomeQuemAgendou: auth.nome,
          observacoesAgendamento: observacoesAgendamento || "",
          listaEspera: listaEsperaRef,
        }),
      );
      let link = `${process.env.REACT_APP_BACKEND_LINK}/api/agenda/efetuarmarcacao/${clinica.clinica._id}`;
      if (showNewClientForm) {
        fd.append("clienteData", JSON.stringify(newClientData));
        link = `${process.env.REACT_APP_BACKEND_LINK}/api/agenda/criarMarcacaoECliente/${clinica.clinica._id}`;
      } else fd.append("nomeCliente", selectedClient.label);
      const res = await sendRequest(link, "PATCH", fd, {
        Authorization: "Bearer " + auth.token,
      });
      const clientName = showNewClientForm
        ? getFirstAndLastName(newClientData.nome)
        : getFirstAndLastName(selectedClient.label);
      const clientId = res.cliente?._id || selectedClient.id;
      updateEvents(
        {
          id: res.addedMarcacaoId,
          start: moment(startDate).format("YYYY-MM-DD HH:mm:ss"),
          end: moment(endDate).format("YYYY-MM-DD HH:mm:ss"),
          title: ` ${clientName} - Avaliação`,
          backgroundColor: "#fde6c5",
          borderColor: "#F57C00",
          textColor: "#F57C00",
          extendedProps: {
            clientId,
            isEvaluation: true,
            isAttended: undefined,
            isMissed: undefined,
            inTransition: false,
            isCompromisso: false,
            isTratamentoIsolado: false,
            agendadoPor: auth.nome,
            observacoesAgendamento: observacoesAgendamento || "",
            listaEsperaId: listaEsperaRef,
          },
        },
        null,
        res.cliente || null,
        houveAlteracao,
        event?.id,
      );
      return;
    }

    // ── TRATAMENTO ISOLADO ───────────────────────────────────────────────────
    if (appointmentType === "tratamentoIsolado") {
      let fd2 = financeData;
      if (!fd2) {
        const [catRes, caixaRes] = await Promise.all([
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/categoriasfinanceiro/clinica/entrada/${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/clinica/${clinica.clinica._id}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          ),
        ]);
        /* Ver a nota no outro sítio que faz isto, mais acima. */
        fd2 = dadosFinanceirosTratamentoIsolado(caixaRes, catRes);
      }

      if (isEdit && !isCreating) {
        const fd = new FormData();
        fd.append("cliente", selectedClient.id);
        fd.append("utilizador", auth.userId);
        fd.append("nomeCliente", selectedClient.label);
        fd.append("data_inicio_execucao", startDate);
        fd.append("data_fim_execucao", endDate);
        fd.append("observacoesAgendamento", observacoesAgendamento);
        fd.append("comEntrada", tratamentoIsoladoComEntradaChecked);
        fd.append("caixa", fd2.caixa._id);
        fd.append("categoriaFinanceiro", fd2.categoria._id);
        fd.append("nomeQuemAgendou", auth.nome);
        // Com entrada → tratamento (tem preço); sem entrada → categoria
        if (tratamentoIsoladoComEntradaChecked)
          fd.append("tratamento", selectedProcedure);
        else fd.append("categoriaTratamento", selectedProcedure);
        /* A resposta à pergunta do ✓ — ver a nota na edição da avaliação. */
        fd.append(
          "manterConfirmacao",
          respostaConfirmacaoRef.current === "manter" ? "true" : "false",
        );
        const res = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentoIsolados/editarAgendamento/${event.id}`,
          "PATCH",
          fd,
          { Authorization: "Bearer " + auth.token },
        );
        const label =
          procedimentos.find((p) => p.id === selectedProcedure)?.label || "";
        const newEv = {
          id: res?.tratamentoIsoladoId || event.id,
          start: startDate,
          end: endDate,
          title: ` ${getFirstAndLastName(selectedClient.label)} - ${label}`,
          backgroundColor: "#D7CCC8",
          borderColor: "#5D4037",
          textColor: "#5D4037",
          extendedProps: {
            ...event.extendedProps,
            clientId: selectedClient.id,
            procedureId: selectedProcedure,
            agendadoPor: auth.nome,
            observacoesAgendamento: observacoesAgendamento || "",
            comEntrada: tratamentoIsoladoComEntradaChecked,
            /* Ver a nota na edição da avaliação: `...event.extendedProps`
               traz o ✓ antigo e ele ficava no ecrã depois de o servidor o
               ter apagado. */
            confirmado:
              respostaConfirmacaoRef.current === "retirar"
                ? false
                : event.extendedProps?.confirmado,
          },
        };
        if (res?.tratamentoIsoladoId && res.tratamentoIsoladoId !== event.id)
          updateEvents(newEv, null, null, true, event.id);
        else updateEvents(newEv, event.id);
        return;
      }

      const tData = {
        data_inicio_execucao: startDate,
        data_fim_execucao: endDate,
        medico: medicoId,
        clinica: clinica.clinica._id,
        observacoesAgendamento: observacoesAgendamento || "",
        relatorio: "",
        agendadoPor: auth.userId,
        nomeQuemAgendou: auth.nome,
        listaEspera: listaEsperaRef,
      };
      // Com entrada → tratamento (tem preço); sem entrada → categoria
      if (tratamentoIsoladoComEntradaChecked)
        tData.tratamento = selectedProcedure;
      else tData.categoriaTratamento = selectedProcedure;
      const eData = {
        tipo: "reembolso",
        clinica: clinica.clinica._id,
        observacoes: "",
        dataLimitePagamento: new Date(),
        categoria: fd2.categoria._id,
        caixa: fd2.caixa._id,
      };
      const fd = new FormData();
      fd.append("nomeMedico", nomeMedico);
      fd.append("comEntrada", tratamentoIsoladoComEntradaChecked);

      if (showNewClientForm) {
        fd.append("tratamentoIsoladoData", JSON.stringify(tData));
        fd.append("entradaData", JSON.stringify(eData));
        fd.append("clienteData", JSON.stringify(newClientData));
        const res = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentoIsolados/criarMarcacaoECliente/${auth.userId}`,
          "POST",
          fd,
          { Authorization: "Bearer " + auth.token },
        );
        updateEvents(
          {
            id: res.tratamentoIsolado._id,
            start: moment(startDate).format("YYYY-MM-DD HH:mm:ss"),
            end: moment(endDate).format("YYYY-MM-DD HH:mm:ss"),
            title: ` ${getFirstAndLastName(res.cliente.nome)} - ${res.tratamentoIsolado.tratamento?.designacao || res.tratamentoIsolado.categoriaTratamento?.categoria}`,
            backgroundColor: "#D7CCC8",
            borderColor: "#5D4037",
            textColor: "#5D4037",
            extendedProps: {
              clientId: res.cliente._id,
              isTratamentoIsolado: true,
              comEntrada: tratamentoIsoladoComEntradaChecked,
              faltou: false,
              atendido: false,
              agendadoPor: auth.nome,
              observacoesAgendamento: observacoesAgendamento || "",
              listaEsperaId: listaEsperaRef,
            },
          },
          null,
          res.cliente,
          houveAlteracao,
          event?.id,
        );
      } else {
        tData.cliente = selectedClient.id;
        eData.cliente = selectedClient.id;
        fd.append("tratamentoIsoladoData", JSON.stringify(tData));
        fd.append("entradaData", JSON.stringify(eData));
        fd.append("nomeCliente", selectedClient.label);
        const res = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentoIsolados/${auth.userId}`,
          "POST",
          fd,
          { Authorization: "Bearer " + auth.token },
        );
        updateEvents(
          {
            id: res.tratamentoIsolado._id,
            start: moment(startDate).format("YYYY-MM-DD HH:mm:ss"),
            end: moment(endDate).format("YYYY-MM-DD HH:mm:ss"),
            title: ` ${getFirstAndLastName(selectedClient.label)} - ${res.tratamentoIsolado.tratamento?.designacao || res.tratamentoIsolado.categoriaTratamento?.categoria}`,
            backgroundColor: "#D7CCC8",
            borderColor: "#5D4037",
            textColor: "#5D4037",
            extendedProps: {
              clientId: selectedClient.id,
              isTratamentoIsolado: true,
              comEntrada: tratamentoIsoladoComEntradaChecked,
              procedureId: selectedProcedure,
              faltou: false,
              atendido: false,
              agendadoPor: auth.nome,
              observacoesAgendamento: observacoesAgendamento || "",
              listaEsperaId: listaEsperaRef,
            },
          },
          null,
          null,
          houveAlteracao,
          event?.id,
        );
      }
      return;
    }

    // ── AGENDAMENTO PROVISÓRIO ───────────────────────────────────────────────
    if (appointmentType === "agendamentoProvisorio") {
      const res = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/agendamentosprovisorio`,
        "POST",
        JSON.stringify({
          clinicaId: clinica.clinica._id,
          medicoId,
          clienteId: selectedClient.id,
          data_inicio_execucao: startDate,
          data_fim_execucao: endDate,
          dataLembrete1Mes,
          dataLembrete1Semana,
          dataLembrete1Dia,
          observacoesAgendamento: observacoesAgendamento || "",
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );
      updateEvents(
        {
          id: res.agendamentoProvisorio._id,
          start: new Date(res.agendamentoProvisorio.data_inicio_execucao),
          end: new Date(res.agendamentoProvisorio.data_fim_execucao),
          title: ` ${getFirstAndLastName(selectedClient.label)} - Agendamento provisório`,
          backgroundColor: "#FFF3E0",
          borderColor: "#FF8F00",
          textColor: "#663C00",
          extendedProps: {
            isAgendamentoProvisorio: true,
            clientId: selectedClient.id,
            agendadoPor: auth.nome,
            observacoesAgendamento:
              res.agendamentoProvisorio.observacoesAgendamento,
          },
        },
        null,
        null,
        houveAlteracao,
        event?.id,
      );
      return;
    }

    // ── PROCEDIMENTO ─────────────────────────────────────────────────────────
    if (appointmentType === "procedimento") {
      const isOrc = selectedProcedure.includes("-orc-");
      if (isEdit && !isCreating) {
        const fd = new FormData();
        fd.append("cliente", selectedClient.id);
        fd.append("nomeCliente", selectedClient.label);
        fd.append("categoriaTratamento", selectedProcedure.split("-")[0]);
        fd.append(
          "nomeNovaCategoria",
          allCategoriasTratamento.find(
            (c) => c.id === selectedProcedure.split("-")[0],
          )?.label || "",
        );
        if (isOrc) fd.append("orcamento", selectedProcedure.split("-orc-")[1]);
        else fd.append("planoTratamento", selectedProcedure.split("-")[1]);
        fd.append("data_inicio_execucao", startDate);
        fd.append("data_fim_execucao", endDate);
        fd.append("observacoesAgendamento", observacoesAgendamento);
        fd.append("agendadoPor", auth.userId);
        fd.append("nomeQuemAgendou", auth.nome);
        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/agenda/alterarparanovamarcacao/${event.id}`,
          "PATCH",
          fd,
          { Authorization: "Bearer " + auth.token },
        );
        updateEvents(
          {
            start: startDate,
            end: endDate,
            title: ` ${getFirstAndLastName(selectedClient.label)} - ${allCategoriasTratamento.find((c) => c.id === selectedProcedure.split("-")[0])?.label || ""}`,
            backgroundColor: isOrc ? "#E0F7FA" : "#ecf6ff",
            borderColor: isOrc ? "#0097A7" : "#1976D2",
            textColor: isOrc ? "#0097A7" : "#1976D2",
            extendedProps: {
              clientId: selectedClient.id,
              procedureId: selectedProcedure.split("-")[0],
              isEvaluation: undefined,
              isAttended: undefined,
              isMissed: undefined,
              inTransition: false,
              isCompromisso: false,
              isTratamentoIsolado: false,
              agendadoPor: auth.nome,
              planoId: !isOrc ? selectedProcedure.split("-")[1] : undefined,
              orcamentoId: isOrc
                ? selectedProcedure.split("-orc-")[1]
                : undefined,
              isOrcamento: isOrc,
              observacoesAgendamento: observacoesAgendamento || "",
            },
          },
          event.id,
        );
        return;
      }
      const fd = new FormData();
      fd.append("nomeMedico", nomeMedico);
      const marcacao = {
        cliente: selectedClient.id,
        data_inicio_execucao: startDate,
        data_fim_execucao: endDate,
        categoriaTratamento: selectedProcedure.split("-")[0],
        agendadoPor: auth.userId,
        nomeQuemAgendou: auth.nome,
        observacoesAgendamento: observacoesAgendamento || "",
        listaEspera: listaEsperaRef,
      };
      if (isOrc) marcacao.orcamento = selectedProcedure.split("-orc-")[1];
      else marcacao.planoTratamento = selectedProcedure.split("-")[1];
      fd.append("medico", medicoId);
      fd.append("marcacao", JSON.stringify(marcacao));
      fd.append("nomeCliente", selectedClient.label);
      fd.append(
        "categoriaDaMarcacao",
        procedimentos.find((p) => p.id === selectedProcedure)?.originalLabel ||
          "",
      );
      const res = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/agenda/efetuarmarcacao/${clinica.clinica._id}`,
        "PATCH",
        fd,
        { Authorization: "Bearer " + auth.token },
      );
      updateEvents(
        {
          id: res.addedMarcacaoId,
          start: moment(startDate).format("YYYY-MM-DD HH:mm:ss"),
          end: moment(endDate).format("YYYY-MM-DD HH:mm:ss"),
          title: ` ${getFirstAndLastName(selectedClient.label)} - ${allCategoriasTratamento.find((c) => c.id === selectedProcedure.split("-")[0])?.label || ""}`,
          backgroundColor: isOrc ? "#E0F7FA" : "#ecf6ff",
          borderColor: isOrc ? "#0097A7" : "#1976D2",
          textColor: isOrc ? "#0097A7" : "#1976D2",
          extendedProps: {
            clientId: selectedClient.id,
            procedureId: selectedProcedure.split("-")[0],
            isEvaluation: undefined,
            isAttended: undefined,
            isMissed: undefined,
            inTransition: false,
            isCompromisso: false,
            isTratamentoIsolado: false,
            agendadoPor: auth.nome,
            planoId: !isOrc ? selectedProcedure.split("-")[1] : undefined,
            orcamentoId: isOrc
              ? selectedProcedure.split("-orc-")[1]
              : undefined,
            isOrcamento: isOrc,
            observacoesAgendamento: observacoesAgendamento || "",
            listaEsperaId: listaEsperaRef,
          },
        },
        null,
        null,
        houveAlteracao,
        event?.id,
      );
    }
  };

  // ── renderSlotFields ──────────────────────────────────────────────────────

  const renderSlotFields = (slot, index) => {
    const col = SLOT_COLORS[index % SLOT_COLORS.length];
    const isOnly = slots.length === 1 && !event;
    const clientesParaSlot =
      slot.appointmentType === "procedimento"
        ? clientesProcedimento
        : [...clients];
    const showAddClientBtn =
      !slot.listaEsperaAtiva &&
      slot.appointmentType !== "procedimento" &&
      slot.appointmentType !== "agendamentoProvisorio";
    const usedListaEsperaIds = new Set(
      slots
        .filter((_, i) => i !== index)
        .map((s) => s.selectedListaEsperaItem?._id)
        .filter(Boolean),
    );
    const listaEsperaDisponivel = (listaEspera || []).filter(
      (i) => !usedListaEsperaIds.has(i._id),
    );

    return (
      <div
        key={slot.id}
        style={{
          border: `1px solid ${col.border}`,
          borderRadius: "10px",
          padding: "14px 14px 10px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          background: col.bg,
        }}
      >
        {/* header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            style={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: col.label,
            }}
          >
            {event ? "Agendamento" : `Sessão ${index + 1}`}
          </span>
          {!isOnly && !event && (
            <button
              type="button"
              onClick={() => removeSlot(index)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "20px",
                lineHeight: 1,
                color: col.label,
                opacity: 0.5,
                padding: "0 2px",
              }}
            >
              ×
            </button>
          )}
        </div>

        {/* lista espera toggle */}
        {!event && listaEsperaDisponivel.length > 0 && (
          <div
            style={{
              borderRadius: "8px",
              border: slot.listaEsperaAtiva
                ? `1px solid #7B1FA2`
                : `1px dashed ${col.border}`,
              background: slot.listaEsperaAtiva ? "#F3E5F5" : "transparent",
              padding: "8px 10px",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={slot.listaEsperaAtiva}
                  onChange={(e) =>
                    handleToggleListaEspera(index, e.target.checked)
                  }
                  size="small"
                  sx={{
                    color: "#7B1FA2",
                    "&.Mui-checked": { color: "#7B1FA2" },
                  }}
                />
              }
              label={
                <span
                  style={{
                    fontSize: "13px",
                    color: slot.listaEsperaAtiva ? "#4A148C" : col.label,
                  }}
                >
                  Agendar da lista de espera
                </span>
              }
            />

            {slot.listaEsperaAtiva && (
              <FormControl fullWidth size="small" style={{ marginTop: "6px" }}>
                <InputLabel>Paciente da lista de espera</InputLabel>
                <Select
                  value={slot.selectedListaEsperaItem?._id || ""}
                  label="Paciente da lista de espera"
                  onChange={(e) => {
                    const item = listaEsperaDisponivel.find(
                      (i) => i._id === e.target.value,
                    );
                    if (item) handleSelectListaEsperaItem(index, item);
                  }}
                >
                  <MenuItem value="">Selecione</MenuItem>
                  {listaEsperaDisponivel.map((item) => (
                    <MenuItem key={item._id} value={item._id}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span>
                          {item.pacienteProvisorio?.nome || item.cliente?.nome}
                        </span>
                        <span style={{ fontSize: "11px", color: "#888" }}>
                          {item.categoriaTratamento?.categoria ||
                            item.tratamento?.designacao ||
                            "Sem procedimento"}
                          {" · "}
                          <span
                            style={{
                              color:
                                item.prioridade === "urgente"
                                  ? "#D32F2F"
                                  : item.prioridade === "normal"
                                    ? "#1976D2"
                                    : "#388E3C",
                              fontWeight: 600,
                            }}
                          >
                            {item.prioridade}
                          </span>
                        </span>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
        )}

        {/* type selector */}
        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
          <FormControl fullWidth>
            <InputLabel>Tipo Agendamento*</InputLabel>
            <Select
              value={slot.appointmentType}
              label="Tipo Agendamento"
              onChange={(e) =>
                handleAppointmentTypeChange(index, e.target.value)
              }
            >
              <MenuItem
                value="procedimento"
                disabled={!!slot.selectedListaEsperaItem?.pacienteProvisorio}
              >
                Procedimento
              </MenuItem>
              {auth.perm.includes("c-consult") && (
                <MenuItem value="tratamentoIsolado">
                  Tratamento Isolado
                </MenuItem>
              )}
              <MenuItem value="avaliacao">Avaliação</MenuItem>
              {!slot.listaEsperaAtiva && (
                <MenuItem value="agendamentoProvisorio">
                  Agendamento Provisório
                </MenuItem>
              )}
              {!slot.listaEsperaAtiva && (
                <MenuItem value="compromisso">Compromisso</MenuItem>
              )}
            </Select>
          </FormControl>
          {index === 0 && (
            <HelpButton
              tutorialData={{
                type: "text",
                title: "Tipos de Agendamento",
                content: `<h4>Procedimento</h4><p>Para agendamentos de orçamentos ou planos de tratamento criados no sistema.</p><h4>Tratamento Isolado</h4><p>Para tratamentos não associados a orçamento ou plano.</p><h4>Avaliação</h4><p>Para agendar uma avaliação médica.</p><h4>Agendamento Provisório</h4><p>Com lembretes automáticos para confirmação.</p><h4>Compromisso</h4><p>Para compromissos gerais.</p>`,
              }}
            />
          )}
        </div>

        {/* dates */}
        <div className="appointment-form__datasContainer">
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePicker
              label="Início"
              value={slot.startDate ? moment(slot.startDate) : null}
              onChange={(v) => {
                const sd = v?.toDate() || null;
                const ed = sd
                  ? new Date(sd.getTime() + duracaoConsulta * 60000)
                  : null;
                const aviso =
                  index > 0
                    ? verificarSobreposicao(index, sd, ed, events, slots)
                    : null;
                updateSlot(index, { startDate: sd, endDate: ed, aviso });
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePicker
              label="Fim"
              value={slot.endDate ? moment(slot.endDate) : null}
              onChange={(v) => {
                const ed = v?.toDate() || null;
                const aviso =
                  index > 0
                    ? verificarSobreposicao(
                        index,
                        slot.startDate,
                        ed,
                        events,
                        slots,
                      )
                    : null;
                updateSlot(index, { endDate: ed, aviso });
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </div>

        {slot.aviso && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 10px",
              borderRadius: "6px",
              backgroundColor: "#FFF8E1",
              border: "1px solid #FFD54F",
              fontSize: "12px",
              color: "#F57F17",
            }}
          >
            <span style={{ fontSize: "14px" }}>⚠️</span>
            {slot.aviso}
          </div>
        )}

        {/* client */}
        {slot.appointmentType !== "compromisso" && !slot.listaEsperaAtiva && (
          <ClientAutocomplete
            selectedClient={slot.selectedClient}
            clients={clientesParaSlot}
            onChange={(val) => handleClienteChange(index, val)}
            disabled={false}
            onAddNewClient={() => handleToggleNewClientForm(index)}
            isNewClientFormActive={slot.showNewClientForm}
            showAddButton={showAddClientBtn}
          />
        )}

        {/* new client form */}
        {slot.showNewClientForm && (
          <div className="appointment-form__new-client">
            <h3>Novo Cliente</h3>
            {[
              { label: "Nome*", name: "nome", type: "text" },
              { label: "Contacto*", name: "contacto", type: "tel" },
              { label: "Endereco*", name: "endereco", type: "text" },
            ].map(({ label, name, type }) => (
              <div className="appointment-form__field" key={name}>
                <label>{label}</label>
                <input
                  type={type}
                  name={name}
                  value={slot.newClientData[name]}
                  onChange={(e) =>
                    updateSlot(index, {
                      newClientData: {
                        ...slot.newClientData,
                        [name]: e.target.value,
                      },
                    })
                  }
                  className="appointment-form__input"
                />
              </div>
            ))}
            <div className="appointment-form__field">
              <label>Data de Nascimento*</label>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DesktopDatePicker
                  inputFormat="DD-MM-YYYY"
                  value={
                    slot.newClientData.data_nasc
                      ? moment(slot.newClientData.data_nasc, "DD-MM-YYYY")
                      : null
                  }
                  onChange={(value) =>
                    updateSlot(index, {
                      newClientData: {
                        ...slot.newClientData,
                        data_nasc:
                          value && value.isValid()
                            ? value.format("DD-MM-YYYY")
                            : "",
                      },
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="data_nasc"
                      className="appointment-form__input"
                    />
                  )}
                />
              </LocalizationProvider>
            </div>
            <div className="appointment-form__field">
              <label>Género*</label>
              <select
                name="genero"
                value={slot.newClientData.genero}
                onChange={(e) =>
                  updateSlot(index, {
                    newClientData: {
                      ...slot.newClientData,
                      genero: e.target.value,
                    },
                  })
                }
                className="appointment-form__select"
              >
                <option value="" disabled>
                  Selecione o género
                </option>
                <option value="feminino">Feminino</option>
                <option value="masculino">Masculino</option>
                <option value="outro">Outro</option>
              </select>
            </div>
          </div>
        )}

        {/* procedimento */}
        {slot.appointmentType === "procedimento" &&
          slot.procedimentos.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>Procedimento*</InputLabel>
              <Select
                value={slot.selectedProcedure}
                label="Procedimento"
                onChange={(e) =>
                  updateSlot(index, { selectedProcedure: e.target.value })
                }
                renderValue={(v) =>
                  slot.procedimentos.find((p) => p.id === v)?.originalLabel ||
                  ""
                }
              >
                <MenuItem value="">Selecione um procedimento</MenuItem>
                {slot.procedimentos.map((p, i) => (
                  <MenuItem
                    key={i + p.id}
                    value={p.id}
                    style={{
                      color: p.isOrcamento ? "#0097A7" : "inherit",
                      fontStyle: p.isOrcamento ? "italic" : "normal",
                    }}
                  >
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

        {/* tratamento isolado — sem entrada usa categoria; com entrada usa tratamento */}
        {slot.appointmentType === "tratamentoIsolado" && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={slot.tratamentoIsoladoComEntradaChecked}
                  onChange={(e) => handleEntradaChange(index, e.target.checked)}
                  color="primary"
                />
              }
              label="Adicionar Entrada"
            />
            {slot.procedimentos.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>
                  {slot.tratamentoIsoladoComEntradaChecked
                    ? "Tratamento*"
                    : "Categoria*"}
                </InputLabel>
                <Select
                  value={slot.selectedProcedure}
                  label={
                    slot.tratamentoIsoladoComEntradaChecked
                      ? "Tratamento"
                      : "Categoria"
                  }
                  onChange={(e) =>
                    updateSlot(index, { selectedProcedure: e.target.value })
                  }
                >
                  <MenuItem value="">
                    {slot.tratamentoIsoladoComEntradaChecked
                      ? "Selecione um tratamento"
                      : "Selecione uma categoria"}
                  </MenuItem>
                  {slot.procedimentos.map((p, i) => (
                    <MenuItem key={i} value={p.id}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        )}

        {/* observações */}
        {slot.appointmentType !== "compromisso" && (
          <div className="appointment-form__field">
            <label>Observações (opcional)</label>
            <textarea
              rows={3}
              value={slot.observacoesAgendamento}
              onChange={(e) =>
                updateSlot(index, { observacoesAgendamento: e.target.value })
              }
              className="appointment-form__compromissoTextarea"
            />
          </div>
        )}

        {/* agendamento provisório lembretes — only index 0 */}
        {slot.appointmentType === "agendamentoProvisorio" && index === 0 && (
          <>
            <div className="appointment-form__field">
              <label>Datas dos Lembretes</label>
            </div>
            <div className="appointment-form__datasContainer">
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="Lembrete 1 Mês Antes"
                  value={dataLembrete1Mes}
                  onChange={setDataLembrete1Mes}
                  renderInput={(p) => <TextField {...p} />}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="Lembrete 1 Semana Antes"
                  value={dataLembrete1Semana}
                  onChange={setDataLembrete1Semana}
                  renderInput={(p) => <TextField {...p} />}
                />
              </LocalizationProvider>
            </div>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                label="Lembrete 1 Dia Antes"
                value={dataLembrete1Dia}
                onChange={setDataLembrete1Dia}
                renderInput={(p) => <TextField {...p} />}
              />
            </LocalizationProvider>
          </>
        )}

        {/* compromisso */}
        {slot.appointmentType === "compromisso" && (
          <div className="appointment-form__field">
            <label>Descrição do Compromisso</label>
            <textarea
              rows={4}
              value={slot.compromisso}
              onChange={(e) =>
                updateSlot(index, { compromisso: e.target.value })
              }
              className="appointment-form__compromissoTextarea"
            />
          </div>
        )}
      </div>
    );
  };

  // ─── JSX ──────────────────────────────────────────────────────────────────

  return (
    <div className={`appointment-form ${isOpen ? "open" : ""}`}>
      <div className="appointment-form__content">
        <h2 className="appointment-form__title">
          {event ? "Editar Agendamento" : "Novo Agendamento"}
        </h2>

        <form onSubmit={handleSubmit}>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {slots.map((slot, index) => renderSlotFields(slot, index))}
          </div>

          {/* add slot button */}
          {!event && (
            <button
              type="button"
              onClick={addSlot}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: "100%",
                padding: "10px 14px",
                border: "1.5px dashed #bbb",
                borderRadius: "10px",
                background: "transparent",
                cursor: "pointer",
                fontSize: "13px",
                color: "#888",
                fontFamily: "inherit",
                marginTop: "4px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#888";
                e.currentTarget.style.color = "#555";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#bbb";
                e.currentTarget.style.color = "#888";
              }}
            >
              <span style={{ fontSize: "18px", lineHeight: 1 }}>+</span>
              adicionar outro horário
              {slots.length > 1 && (
                <span
                  style={{
                    fontSize: "11px",
                    padding: "2px 8px",
                    borderRadius: "99px",
                    background: "#eee",
                    color: "#999",
                    fontWeight: 500,
                  }}
                >
                  {slots.length} sessões
                </span>
              )}
            </button>
          )}

          <div className="appointment-form__errorContainer">
            {menssagemDeErro}
          </div>

          <div className="appointment-form__actions">
            <button
              type="button"
              onClick={onClose}
              className="appointment-form__close-btn"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="appointment-form__submit-btn"
              disabled={aGravar}
            >
              {aGravar
                ? "A gravar…"
                : event
                  ? "Atualizar"
                  : slots.length > 1
                    ? `Agendar ${slots.length} sessões`
                    : "Agendar"}
            </button>
          </div>
        </form>
      </div>

      {/* A pergunta do ✓, quando a data de uma marcação confirmada muda.
          A resposta vai para o `ref` ANTES de repetir o `handleSubmit`, e é
          por isso que basta um clique: um `setTimeout` com estado não servia,
          porque o `handleSubmit` chamado é o closure do render anterior e
          continuaria a ler a resposta vazia. */}
      <DialogoConfirmacaoAgendamento
        aberto={!!perguntaConfirmacao}
        modo="mover"
        nomeCliente={perguntaConfirmacao?.nomeCliente}
        aoManter={() => {
          respostaConfirmacaoRef.current = "manter";
          setPerguntaConfirmacao(null);
          handleSubmit({ preventDefault: () => {} });
        }}
        aoRetirar={() => {
          respostaConfirmacaoRef.current = "retirar";
          setPerguntaConfirmacao(null);
          handleSubmit({ preventDefault: () => {} });
        }}
        aoCancelar={() => setPerguntaConfirmacao(null)}
      />

      {/* analise modal */}
      {analiseClienteModal.show && (
        <div className="appointment-form__modal-overlay">
          <div className="appointment-form__modal">
            <h3>⚠️ Atenção</h3>
            <ul>
              {analiseClienteModal.alertas.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
            <p>Deseja continuar com o agendamento?</p>
            <div className="appointment-form__modal-actions">
              <button
                type="button"
                onClick={() =>
                  setAnaliseClienteModal({
                    show: false,
                    alertas: [],
                    onConfirm: null,
                  })
                }
                className="appointment-form__close-btn"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={analiseClienteModal.onConfirm}
                className="appointment-form__submit-btn"
              >
                Continuar Mesmo Assim
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentForm;
